import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "./client";

export type RbpeAlertRuleScope =
  | { type: "deal_floor"; ravFloorPct: number }
  | { type: "segment_drift"; relativeMovePct: number; absoluteFloorBps: number };

export interface RbpeAlertRule {
  id: string;
  scope: RbpeAlertRuleScope;
  officeScope: "all" | string[];
  active: boolean;
  notificationRecipients: string[];
}

export interface RbpeAlertRuleProposal {
  id: string;
  scope: RbpeAlertRuleScope;
  reason: string;
  proposedAt: string;
  status: "pending" | "approved" | "rejected";
}

export function describeScope(scope: RbpeAlertRuleScope) {
  if (scope.type === "deal_floor") {
    return `Deal falls below floor · RAV floor ${scope.ravFloorPct.toFixed(2)}%`;
  }
  return `Segment drifts below floor · relative move ${scope.relativeMovePct.toFixed(1)}% · absolute floor ${scope.absoluteFloorBps} bps`;
}

export const rbpeAlertRuleKeys = {
  rules: () => ["rbpe-alert-rules", "rules"] as const,
  proposals: () => ["rbpe-alert-rules", "proposals"] as const,
};

export function fetchRbpeAlertRules() {
  return apiGet<RbpeAlertRule[]>("/alert-rules");
}

export function fetchRbpeAlertRuleProposals() {
  return apiGet<RbpeAlertRuleProposal[]>("/alert-rules/proposals");
}

export function useRbpeAlertRules() {
  return useQuery({
    queryKey: rbpeAlertRuleKeys.rules(),
    queryFn: fetchRbpeAlertRules,
    staleTime: 60_000,
  });
}

export function useRbpeAlertRuleProposals() {
  return useQuery({
    queryKey: rbpeAlertRuleKeys.proposals(),
    queryFn: fetchRbpeAlertRuleProposals,
    staleTime: 60_000,
  });
}

export interface RbpeAlertRuleDraft {
  scope: RbpeAlertRuleScope;
  officeScope: "all" | string[];
  active: boolean;
  notificationRecipients: string[];
  reason: string;
}

export function useProposeRbpeAlertRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (draft: RbpeAlertRuleDraft) =>
      apiPost<RbpeAlertRuleProposal>("/alert-rules/proposals", draft),
    onSuccess: () => {
      // The proposal is now real (persisted in alert_rule_proposals) --
      // refetch rather than guess its server-assigned id/proposedAt.
      queryClient.invalidateQueries({ queryKey: rbpeAlertRuleKeys.proposals() });
    },
  });
}

export function useDecideRbpeAlertRuleProposal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "approved" | "rejected" }) => {
      await apiPost(`/alert-rules/proposals/${id}/decide`, { status });
      return { id, status };
    },
    onSuccess: () => {
      // Approving now really registers a live Superset alert and extends the
      // queue's status server-side -- refetch both instead of hand-patching a
      // guessed shape (matches the fix already applied to Manage Targets).
      queryClient.invalidateQueries({ queryKey: rbpeAlertRuleKeys.proposals() });
      queryClient.invalidateQueries({ queryKey: rbpeAlertRuleKeys.rules() });
    },
  });
}
